/**
 * Backend `err_*` codes → user-facing copy for the scheduling wizard.
 *
 * Keep this map scoped to scheduling errors; cross-feature codes
 * (e.g. generic auth, calendar integration) stay in their own
 * feature's map.
 *
 * Usage:
 *   import { resolveScheduleErrorMessage } from '@/pages/InterviewManager/ScheduleInterview/config/errorCopy';
 *   toast({ title: resolveScheduleErrorMessage(err), status: 'error' });
 */
import { ApiError, resolveErrorMessage } from '@/api/request';

export const SCHEDULE_ERROR_COPY: Record<string, string> = {
  err_no_cal_connected: 'Connect your calendar in Settings before scheduling.',
  err_invalid_time_range: 'Pick an end time that is after the start time.',
  err_invalid_week_start_date: 'The week must start on a Sunday.',
  err_time_parsing: 'We could not read that date. Please try another.',
  err_missing_job_stage_action:
    'This interview slot is missing from the job plan — refresh and retry.',
  err_invalid_interview_ats_job_stage_action:
    'This recommendation is only available for one-on-one interviews.',
  err_interviewers_list_or_pool_not_configured:
    'Add at least one interviewer to the interview plan first.',
  err_duplicate_meeting_room_email_for_interview:
    'This meeting room is already booked for this interview.',
  err_note_taker_mandatory:
    'Your admin requires an AI note-taker for interviews in this job.',
  err_name_too_long: 'Interview name is too long.',
  err_start_time_less_than_current_time:
    'Booked event is in the past, please fix',
  err_email_delegation_disabled:
    'Email send on behalf disabled for the from field',
  err_host_id_invalid: 'The host is invalid, does not have zoom access',
  err_contact_duplicate_interview_for_same_time:
    'An interview has already been scheduled for this contact at the selected time. Please choose a different time.',

  // Sender mailbox not connected (raised when the picked From user has no
  // mailbox linked to Kula). Title-only toasts — no description.
  err_sender_mailbox_not_connected: 'From mailbox is not connected.',
  err_candidate_email_sender_mailbox_not_connected:
    "Candidate's From mailbox is not connected.",
  err_interviewer_email_sender_mailbox_not_connected:
    "Interviewer's From mailbox is not connected.",

  // Interview plan validation
  err_interview_type_invalid: 'Select a valid interview type.',
  err_interview_duration_invalid:
    'Duration must be between 1 and 1440 minutes.',
  err_interview_location_invalid: 'Select a valid interview location.',
  err_interview_office_id_required:
    'Select an office for the onsite interview.',
  err_interview_office_not_found:
    'The selected office was not found. Please pick another.',
  err_both_interview_pool_and_manual_selection_cannot_be_true:
    "Interviewer pool and manual selection can't both be enabled.",
  err_either_manual_selection_or_interview_pool_required:
    'Add interviewers manually or select an interviewer pool.',
  err_interview_pool_id_required: 'Select an interviewer pool.',
  err_participant_ids_required: 'Add at least one interviewer.',
};

export function resolveScheduleErrorMessage(
  err: unknown,
  fallback?: string
): string {
  if (err instanceof ApiError && err.code && SCHEDULE_ERROR_COPY[err.code]) {
    return SCHEDULE_ERROR_COPY[err.code];
  }
  return resolveErrorMessage(err, fallback);
}
